import "./index";
import "./App.css";
import "owl.carousel";
import { Fragment, memo, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Route, Routes, matchRoutes, useLocation } from "react-router-dom";
import { MainLayout } from "./Layout/MainLayout/MainLayout";
import { BottomLayout } from "./Layout/BottomLayout/BottomLayout";
import { routesConfig, routesWithoutLogin, adminUserPages, notFoundRoute, routeTitles, PAGES } from "./routeConfig";
import { setPageTitle } from "./utils/pageTitle";

import { useAuth } from "./Context/AuthContext";
import { SuspenseLoader } from "./Layout/Common/loader";
import { useGlobalFunction } from "./Context/GlobalFunctionContext";
import ProtectedRoute from "./protectedRoute";
import CheckRoute from "./checkRoute";
import { axiosInstance } from "./axios/axiosInstance";
import ModalCleanup from "./Context/ModelCleanup";
import { GuestHomeMain } from "./View/HomeGuest/guestHomeMain";
import { useRouteTrail } from "./Context/hook/routeTrail";
import { startOwlAutoplayVisibility } from "./utils/owlAutoplayVisibility";
import { mountWeshubWidget, unmountWeshubWidget } from "./utils/weshubWidget";
import { ConnectionBanner } from "./Layout/Common/connectionBanner";
import { ProductPageOverlay } from "./View/ProductHybrid/productPageOverlay/productPageOverlay";
import {
  OVERLAY_EXIT_MS,
  isLiveLayer,
  readProductStack,
} from "./View/ProductHybrid/productPageOverlay/productPageOverlayContext";
import { useLayerPresence } from "./utils/useLayerPresence";
import { useLayerScroll } from "./View/ProductHybrid/productPageOverlay/useLayerScroll";
import { useProductLinkInterceptor } from "./View/ProductHybrid/productPageOverlay/useProductLinkInterceptor";
import { StableLocationProvider } from "./View/ProductHybrid/productPageOverlay/stableLocationProvider";

const MainRoutes = memo(({ isAuthenticated, role }) => (
  <Routes>
    {routesWithoutLogin.map(
      ({ path, Component, redirectIfAuthenticated, sessionGuardWhenAuthenticated }, index) => (
        <Route
          key={index}
          path={path}
          element={
            <CheckRoute
              Component={Component}
              redirectIfAuthenticated={redirectIfAuthenticated}
              sessionGuardWhenAuthenticated={sessionGuardWhenAuthenticated}
            />
          }
        />
      ),
    )}

    {routesConfig.map(({ path, Component, restrictBrowseOnly, allowGuest }, index) => (
      <Route
        key={50 + index}
        path={path}
        element={
          allowGuest && !isAuthenticated ? (
            <GuestHomeMain />
          ) : (
            <ProtectedRoute Component={Component} restrictBrowseOnly={restrictBrowseOnly} />
          )
        }
      />
    ))}

    {role == 1 &&
      adminUserPages.map(({ path, Component }, index) => (
        <Route key={40 + index} path={path} element={<ProtectedRoute Component={Component} />} />
      ))}

    {/* Any unmatched URL falls through to the 404 page. Keep last. */}
    <Route path={notFoundRoute.path} element={<notFoundRoute.Component />} />
  </Routes>
));

function App() {
  const { isAuthenticated, authLoading, checkAuthentication, role, priceCode } = useAuth();
  const [isPublicUrl, setIsPublicUrl] = useState(null);
  const location = useLocation();
  const { background: pageLocation, layers } =
    isAuthenticated === true ? readProductStack(location) : { background: location, layers: [] };
  const overlayLayers = useLayerPresence(layers, OVERLAY_EXIT_MS);
  const hasOverlay = layers.length > 0;
  const baseTitleRef = useRef({ key: null, title: "" });
  const hadOverlayRef = useRef(false);
  useLayerScroll(hasOverlay ? layers[layers.length - 1].key : pageLocation.key, hasOverlay);
  useRouteTrail();
  useProductLinkInterceptor(isAuthenticated === true);
  const {
    refreshCartItem,
    loadAllWishList,
    fetchNewProductsAlgolia,
    fetchClearanceProductsAlgolia,
    fetchTrendingItemsAlgolia,
    fetchSpecialProductsAlgolia,
    fetchCategorySliderData,
    fetchLinkedCategoryData,
    fetchNewCategory,
  } = useGlobalFunction();

  useEffect(() => {
    fetchLinkedCategoryData();
    fetchCategorySliderData();
    fetchNewCategory();
  }, []);

  useEffect(() => {
    fetchNewProductsAlgolia();
    fetchClearanceProductsAlgolia();
    fetchSpecialProductsAlgolia();
    fetchTrendingItemsAlgolia();
  }, [priceCode]);

  useEffect(() => {
    try {
      if (isPublicUrl === false) {
        if (isAuthenticated && authLoading === false) {
          refreshCartItem();
          loadAllWishList();
        }
      }
    } catch (error) {
    }
  }, [isPublicUrl, isAuthenticated, authLoading, pageLocation.pathname]);

  useLayoutEffect(() => {
    window.history.scrollRestoration = "manual";
  }, []);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pageLocation.pathname]);

  useEffect(() => {
    try {
      const publicUrl = routesWithoutLogin.map((url) => {
        return url.path;
      });
      if (publicUrl) {
        const isPublicPage = publicUrl?.some((page) => pageLocation.pathname.toLowerCase().includes(page.toLowerCase()));
        setIsPublicUrl(isPublicPage);
      }
    } catch (e) {
      console.error("app.js", e);
    }
  }, [pageLocation.pathname]);

  const applyRouteTitle = (pathname) => {
    const matches = matchRoutes(routeTitles, pathname) ?? [];
    const matched = matches[matches.length - 1];
    const title = matched?.route?.title;
    setPageTitle(typeof title === "function" ? title(matched.params ?? {}) : title);
  };

  // Browser tab title. Set from the matched route on every navigation; pages
  // named after their own data (product, category, brand, search term) overwrite
  // it afterwards through usePageTitle. Deliberately keyed on the pathname only
  // - a query-string change must not wipe a title a page has already refined.
  useEffect(() => {
    applyRouteTitle(pageLocation.pathname);
  }, [pageLocation.pathname]);

  useEffect(() => {
    if (hasOverlay) {
      hadOverlayRef.current = true;
      return;
    }
    if (hadOverlayRef.current) {
      hadOverlayRef.current = false;
      if (baseTitleRef.current.key === pageLocation.key) document.title = baseTitleRef.current.title;
      else applyRouteTitle(pageLocation.pathname);
      return;
    }
    baseTitleRef.current = { key: pageLocation.key, title: document.title };
  });

  useEffect(() => {
    checkAuthentication();
  }, [isPublicUrl]);

  useEffect(() => startOwlAutoplayVisibility(), []);

  useEffect(() => {
    if (isAuthenticated !== true) {
      unmountWeshubWidget();
      return undefined;
    }
    return mountWeshubWidget();
  }, [isAuthenticated]);

  return (
    <Fragment>
      <ModalCleanup />
      {isAuthenticated === null ? (
        <SuspenseLoader show />
      ) : (
        <Fragment>
          <MainLayout />
          <ConnectionBanner />
          <StableLocationProvider location={pageLocation}>
            <main hidden={hasOverlay}>
              <MainRoutes isAuthenticated={isAuthenticated} role={role} />
            </main>
          </StableLocationProvider>
          {overlayLayers.map(
            ({ location: layerLocation, leaving }, depth) =>
              isLiveLayer(depth, leaving, layers.length) && (
                <Routes key={layerLocation.key} location={layerLocation}>
                  <Route
                    path={PAGES.HYDPAGE}
                    element={
                      <ProductPageOverlay
                        leaving={leaving}
                        depth={depth}
                        isTop={depth === layers.length - 1}
                        background={pageLocation}
                        below={layers.slice(0, depth)}
                        layerKey={layerLocation.key}
                      />
                    }
                  />
                  <Route path="*" element={null} />
                </Routes>
              ),
          )}
          <BottomLayout />
        </Fragment>
      )}
    </Fragment>
  );
}

export default App;
